import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { getDigilockerStatusApi } from "../api/verification";
import { useAuth } from "../hooks/useAuth";
import { C, font } from "../styles/tokens";

export function DigiLockerCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshLocalUser } = useAuth();

  const verificationId = searchParams.get("verification_id");
  const isSimulated = searchParams.get("simulated") === "true";

  const [status, setStatus] = useState("PROCESSING"); // "PROCESSING" | "SUCCESS" | "FAILED" | "CONSENT_DENIED" | "EXPIRED"
  const [errorMessage, setErrorMessage] = useState(null);
  const [verifiedData, setVerifiedData] = useState(null);

  useEffect(() => {
    if (!verificationId) {
      setStatus("FAILED");
      setErrorMessage("No verification session ID found in callback URL.");
      return;
    }

    let isMounted = true;
    let pollCount = 0;
    const maxPolls = 10;

    async function checkStatus() {
      try {
        const res = await getDigilockerStatusApi(verificationId);

        if (!isMounted) return;

        const payload = res?.data || res;

        if (
          payload?.status === "VERIFIED" ||
          payload?.status === "AUTHENTICATED"
        ) {
          const token = payload.verificationToken;
          const identityData =
            payload.identity ||
            payload.verification ||
            payload.userDetails ||
            {};
          setStatus("SUCCESS");
          setVerifiedData(identityData);
          setTimeout(() => {
            if (isMounted) {
              navigate("/register", {
                replace: true,
                state: {
                  verificationToken: token,
                  name: identityData.name,
                  maskedAadhaar: identityData.maskedAadhaar,
                  address: identityData.address,
                  dob: identityData.dateOfBirth || identityData.dob,
                  gender: identityData.gender,
                  phone: identityData.phone || identityData.mobile || undefined,
                  mobile:
                    identityData.mobile || identityData.phone || undefined,
                  verifiedVia: "DIGILOCKER",
                },
              });
            }
          }, 1500);
        } else if (payload?.status === "CONSENT_DENIED") {
          setStatus("CONSENT_DENIED");
          setErrorMessage(
            payload.message || "Consent was denied on DigiLocker.",
          );
        } else if (payload?.status === "EXPIRED") {
          setStatus("EXPIRED");
          setErrorMessage(payload.message || "DigiLocker session expired.");
        } else if (payload?.status === "FAILED") {
          setStatus("FAILED");
          setErrorMessage(payload.message || "DigiLocker verification failed.");
        } else {
          // PENDING -- retry status polling up to maxPolls
          pollCount++;
          if (pollCount < maxPolls) {
            setTimeout(checkStatus, 2000);
          } else {
            setStatus("FAILED");
            setErrorMessage("Verification timed out. Please try again.");
          }
        }
      } catch (err) {
        if (!isMounted) return;
        setStatus("FAILED");
        setErrorMessage(err?.message || "Failed to verify DigiLocker status.");
      }
    }

    checkStatus();

    return () => {
      isMounted = false;
    };
  }, [verificationId, navigate]);

  return (
    <div
      className="min-h-screen bg-[#F5F7F9] flex flex-col justify-center items-center px-4 py-12"
      style={{ fontFamily: font }}
    >
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-[#E4E8EF] p-8 text-center">
        {/* Header Icon */}
        <div className="mb-6 flex justify-center">
          <div className="h-16 w-16 rounded-full bg-[#E7F4EC] flex items-center justify-center border border-[#08833E]/20">
            <svg
              className="w-8 h-8 text-[#08833E]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          </div>
        </div>

        <h1 className="text-2xl font-bold text-[#16294C] mb-2">
          DigiLocker Identity Verification
        </h1>
        <p className="text-sm text-[#4E5D78] mb-6">
          Cashfree Secure ID Authentication Service
        </p>

        {/* Status Views */}
        {status === "PROCESSING" && (
          <div className="space-y-4 py-4">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-[#08833E] border-t-transparent"></div>
            <p className="text-sm text-[#4E5D78] font-medium">
              Verifying document authentication with Cashfree...
            </p>
            {isSimulated && (
              <span className="inline-block bg-amber-50 text-amber-700 text-xs px-2.5 py-1 rounded-full font-medium border border-amber-200">
                Sandbox Environment Simulated Flow
              </span>
            )}
          </div>
        )}

        {status === "SUCCESS" && (
          <div className="space-y-4 py-2">
            <div className="bg-[#DCF5E7] text-[#057642] text-sm p-4 rounded-lg border border-[#057642]/20 font-medium">
              Aadhaar eKYC Verified Successfully!
            </div>
            {verifiedData?.maskedAadhaar && (
              <div className="text-xs text-[#4E5D78] bg-slate-50 p-3 rounded border border-slate-200">
                <div>
                  <span className="font-semibold text-slate-700">
                    Aadhaar Number:
                  </span>{" "}
                  {verifiedData.maskedAadhaar}
                </div>
                {verifiedData.name && (
                  <div>
                    <span className="font-semibold text-slate-700">Name:</span>{" "}
                    {verifiedData.name}
                  </div>
                )}
              </div>
            )}
            <p className="text-xs text-[#8290A9]">
              Proceeding to subscription payment & registration...
            </p>
          </div>
        )}

        {(status === "FAILED" ||
          status === "CONSENT_DENIED" ||
          status === "EXPIRED") && (
          <div className="space-y-4 py-2">
            <div className="bg-[#FCEBE7] text-[#B24020] text-sm p-4 rounded-lg border border-[#B24020]/20 font-medium">
              {errorMessage || "Verification could not be completed."}
            </div>
            <p className="text-xs text-[#8290A9]">
              {status === "CONSENT_DENIED"
                ? "You chose not to share Aadhaar consent via DigiLocker."
                : "Please initiate a new verification from your registration or profile page."}
            </p>
            <Link
              to="/register"
              className="inline-block w-full py-2.5 px-4 bg-[#16294C] hover:bg-[#0E1C36] text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Return to Sign Up
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
